function normalizeStringArray(value) {
  return Array.isArray(value)
    ? value.map((entry) => String(entry || "").trim()).filter(Boolean)
    : [];
}

function normalizeNumeric(value, fallback = null) {
  if (value == null || value === "") {
    return fallback;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizeTempoValue(value) {
  return String(value || "").replace(/\D/g, "").slice(0, 4) || null;
}

function parseRestSeconds(value) {
  const parsed = Number.parseInt(String(value || "").replace(/\D/g, ""), 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function getExerciseName(value) {
  if (value && typeof value === "object") {
    return String(value.name || "").trim();
  }

  return String(value || "").trim();
}

function hasEditIntent(value, field) {
  return Boolean(value?.editIntent?.[field]);
}

const REP_TARGET_MODES = new Set(["reps", "repRange", "seconds"]);

// D36 -- the canonical rep-prescription modes are derived from field nullity:
// fixed reps (`targetReps`), rep range (`minReps`/`maxReps`) and time
// (`targetSeconds`). The mode of an explicit edit travels on
// `editIntent.repTarget`; the pre-D36 scalar `editIntent.reps` is fixed reps.
// No intent at all means: preserve the persisted prescription verbatim.
function resolveRepTargetMode(set) {
  const mode = set?.editIntent?.repTarget?.mode;

  if (REP_TARGET_MODES.has(mode)) {
    return mode;
  }

  return hasEditIntent(set, "reps") ? "reps" : null;
}

function getMeasurementType(exercise) {
  return exercise?.executionProfile?.measurementType || "REPS";
}

// An explicit rep-target edit writes all four fields atomically so no stale
// value from the previous mode can survive a mode transition.
function buildRepTarget(set, mode) {
  if (mode === "repRange") {
    return {
      targetReps: null,
      minReps: normalizeNumeric(set?.minReps, null),
      maxReps: normalizeNumeric(set?.maxReps, null),
      targetSeconds: null,
    };
  }

  if (mode === "seconds") {
    return {
      targetReps: null,
      minReps: null,
      maxReps: null,
      targetSeconds: normalizeNumeric(set?.targetSeconds, null),
    };
  }

  return {
    targetReps: normalizeNumeric(set?.reps, null),
    minReps: null,
    maxReps: null,
    targetSeconds: null,
  };
}

function normalizeMachineSettings(value) {
  return Array.isArray(value)
    ? value
        .map((setting) => ({
          key: String(setting?.key || "").trim().toLowerCase(),
          value: String(setting?.value ?? ""),
        }))
        .filter((setting) => setting.key || setting.value)
    : [];
}

function normalizeCardioPrescriptionForApi(value = {}, backing = {}) {
  return {
    ...backing,
    durationMinutes: normalizeNumeric(value.durationMinutes, null),
    heartRateTargetMode: value.heartRateTargetMode || undefined,
    heartRateTargetValue: normalizeNumeric(value.heartRateTargetValue, null),
    machineSettings: normalizeMachineSettings(value.machineSettings),
    notes: value.notes ? String(value.notes) : null,
  };
}

function mapSetTemplate(set, index, options = {}) {
  const backing = set?.persistence || null;
  const isPersisted = Boolean(backing);
  const rir = normalizeNumeric(set?.rpe, 2);
  const repTargetMode = resolveRepTargetMode(set);
  const timedEdit = options.measurementType === "TIMED_HOLD" &&
    repTargetMode === "seconds";
  const result = isPersisted
    ? { ...backing }
    : {
        setType: "WORKING",
        ...buildRepTarget(set, repTargetMode || "reps"),
        targetRir: rir,
        targetRpe: null,
        tempo: options.tempo,
        restSeconds: options.restSeconds,
        notes: null,
      };

  result.id = set?.id || backing?.id || undefined;
  result.setIndex = index + 1;

  if (repTargetMode) {
    Object.assign(result, buildRepTarget(set, repTargetMode));
  }

  if (hasEditIntent(set, "rir")) {
    result.targetRir = rir;
  }

  if (options.tempoEdited) {
    result.tempo = options.tempo;
  }

  if (options.restEdited) {
    result.restSeconds = options.restSeconds;
  }

  if (timedEdit) {
    result.targetRir = null;
    result.tempo = null;
  }

  return result;
}

function mapExercise(exercise, index, options = {}) {
  const backing = exercise?.persistence || null;
  const isPersisted = Boolean(backing);
  const measurementType = getMeasurementType(exercise);
  const timedEdit = measurementType === "TIMED_HOLD" &&
    (exercise?.sets || []).some(
      (set) => resolveRepTargetMode(set) === "seconds"
    );
  const result = isPersisted
    ? { ...backing }
    : {
        executionNotes: null,
        defaultTempo: options.tempo,
        defaultRestSeconds: options.restSeconds,
        defaultTargetRir: null,
        defaultTargetRpe: null,
        intensificationMethod: "NONE",
        cardioPrescription: null,
        notes: null,
      };

  result.id = exercise?.id || backing?.id || undefined;
  result.exerciseId = exercise?.exerciseId || null;
  result.exerciseName = String(exercise?.name || "").trim();
  result.bodyParts = normalizeStringArray(exercise?.bodyParts);
  result.muscleFocus = normalizeStringArray(exercise?.muscleFocus);
  result.orderIndex = index + 1;

  if (hasEditIntent(exercise, "notes")) {
    result.executionNotes = exercise.notes || null;
    result.notes = exercise.notes || null;
  }

  if (hasEditIntent(exercise, "tempo")) {
    result.defaultTempo = options.tempo;
  }

  if (timedEdit) {
    result.defaultTempo = null;
    result.defaultTargetRir = null;
  }

  if (options.restEdited) {
    result.defaultRestSeconds = options.restSeconds;
  }

  result.setTemplates = Array.isArray(exercise?.sets)
    ? exercise.sets.map((set, setIndex) =>
        mapSetTemplate(set, setIndex, {
          tempo: options.tempo,
          tempoEdited: hasEditIntent(exercise, "tempo"),
          restSeconds: options.restSeconds,
          restEdited: options.restEdited,
          measurementType,
        })
      )
    : [];

  return result;
}

function mapCardioBlockToApi(block, index) {
  const blockBacking = block.persistence || null;
  const exerciseBacking = block.exercisePersistence || null;
  const cardioEdited = hasEditIntent(block, "cardioPrescription");
  const cardioPrescription = cardioEdited || !exerciseBacking
    ? normalizeCardioPrescriptionForApi(
        block.cardioPrescription || {},
        exerciseBacking?.cardioPrescription || {}
      )
    : exerciseBacking.cardioPrescription;
  const blockResult = blockBacking
    ? { ...blockBacking }
    : {
        blockType: "CARDIO",
        label: null,
        roundCount: null,
        restStrategy: "NONE",
        restSeconds: null,
        notes: null,
      };
  const exerciseResult = exerciseBacking
    ? { ...exerciseBacking }
    : {
        bodyParts: [],
        muscleFocus: [],
        executionNotes: null,
        defaultTempo: null,
        defaultRestSeconds: null,
        defaultTargetRir: null,
        defaultTargetRpe: null,
        intensificationMethod: "NONE",
        notes: null,
      };

  blockResult.id = block.id || blockBacking?.id || undefined;
  blockResult.orderIndex = index + 1;
  blockResult.blockType = "CARDIO";
  exerciseResult.id = block.exerciseRowId || exerciseBacking?.id || undefined;
  exerciseResult.exerciseId = block.exerciseId || block.exercise?.exerciseId || null;
  exerciseResult.exerciseName = getExerciseName(block.exercise);
  exerciseResult.orderIndex = 1;
  exerciseResult.cardioPrescription = cardioPrescription;
  exerciseResult.setTemplates = [];
  blockResult.exercises = [exerciseResult];

  return blockResult;
}

function mapSingleBlockToApi(block, index) {
  const blockBacking = block.persistence || null;
  const blockResult = blockBacking
    ? { ...blockBacking }
    : {
        blockType: "SINGLE",
        label: null,
        roundCount: null,
        restStrategy: "AFTER_EXERCISE",
        restSeconds: parseRestSeconds(block.rest),
        notes: block.notes || null,
      };
  const tempo = normalizeTempoValue(block.tempo);
  const restSeconds = parseRestSeconds(block.rest);
  const exercise = {
    id: block.exerciseRowId,
    name: block.exercise,
    exerciseId: block.exerciseId,
    executionProfile: block.executionProfile || null,
    bodyParts: block.bodyParts,
    muscleFocus: block.muscleFocus,
    notes: block.notes,
    sets: block.sets,
    persistence: block.exercisePersistence,
    editIntent: {
      notes: hasEditIntent(block, "notes"),
      tempo: hasEditIntent(block, "tempo"),
    },
  };

  blockResult.id = block.id || blockBacking?.id || undefined;
  blockResult.orderIndex = index + 1;
  blockResult.blockType = "SINGLE";

  if (hasEditIntent(block, "rest")) {
    blockResult.restSeconds = restSeconds;
  }

  if (hasEditIntent(block, "notes")) {
    blockResult.notes = block.notes || null;
  }

  blockResult.exercises = [
    mapExercise(exercise, 0, {
      tempo,
      restSeconds,
      restEdited: hasEditIntent(block, "rest"),
    }),
  ];

  return blockResult;
}

function mapSupersetBlockToApi(block, index) {
  const blockBacking = block.persistence || null;
  const blockResult = blockBacking
    ? { ...blockBacking }
    : {
        blockType: "SUPERSET",
        label: null,
        roundCount: normalizeNumeric(block.sets, 1),
        restStrategy: "AFTER_ROUND",
        restSeconds: parseRestSeconds(block.rest),
        notes: block.notes || null,
      };
  const restSeconds = parseRestSeconds(block.rest);
  const restEdited = hasEditIntent(block, "rest");

  blockResult.id = block.id || blockBacking?.id || undefined;
  blockResult.orderIndex = index + 1;
  blockResult.blockType = "SUPERSET";

  if (hasEditIntent(block, "roundCount")) {
    blockResult.roundCount = normalizeNumeric(block.sets, 1);
  }

  if (restEdited) {
    blockResult.restSeconds = restSeconds;
  }

  if (hasEditIntent(block, "notes")) {
    blockResult.notes = block.notes || null;
  }

  blockResult.exercises = (block.exercises || []).map((exercise, exerciseIndex) =>
    mapExercise(exercise, exerciseIndex, {
      tempo: normalizeTempoValue(exercise.tempo),
      restSeconds,
      restEdited,
    })
  );

  return blockResult;
}

export function mapBuilderBlockToApi(block, index) {
  if (block?.persistence?.opaqueBlock) {
    return block.persistence.opaqueBlock;
  }

  if (block.type === "cardio") {
    return mapCardioBlockToApi(block, index);
  }

  if (block.type === "superset") {
    return mapSupersetBlockToApi(block, index);
  }

  return mapSingleBlockToApi(block, index);
}

export function mapBuilderWorkoutToApi(workout, index, options = {}) {
  const backing = workout.persistence || {};
  const result = {
    id: workout.id || backing.id,
    name: String(workout.name || "").trim(),
    orderIndex: index + 1,
    estimatedDurationMinutes: backing.estimatedDurationMinutes ?? null,
    notes: backing.notes ?? null,
    blocks: (workout.blocks || []).map(mapBuilderBlockToApi),
  };

  if (options.includeScheduledDay) {
    result.scheduledDay = Object.prototype.hasOwnProperty.call(workout, "scheduledDay")
      ? workout.scheduledDay || null
      : backing.scheduledDay || null;
  }

  return result;
}
